import React, { useState } from "react";
import Button, { Icons } from "../../components/buttons/Button";
import HexInput from "../HexInput";
import {
  CREATE_XSSI_CONNECTION_TRANSLATIONS,
  EDIT_XSSI_CONNECTION_TRANSLATIONS,
} from "../../translations/util";
import {
  XSSI_GROUP_RANGE_MAX_VALUE,
  XSSI_RANGE_MIN_VALUE,
  XSSI_UNIT_RANGE_MAX_VALUE,
} from "../../constants";

export interface IProps {
  addToRange: (min: number, max: number) => void;
  labelPrefix: string;
  loading?: boolean;
  addRangeTitle?: string;
}

const AddRange = ({
  addToRange,
  labelPrefix,
  loading = false,
  addRangeTitle = "Create New",
}: IProps) => {
  const [min, setMin] = useState<number | undefined>();
  const [max, setMax] = useState<number | undefined>();

  const isMaxInvalid = (
    newMax?: number,
    newMin?: number,
    upperLimit?: number
  ) => {
    if (newMax === undefined) {
      return false;
    }

    if (upperLimit && newMax > upperLimit) return true;
    if (newMax <= (newMin || 1)) return true;
    if (newMax < 1) return true;

    return false;
  };

  const isMinInvalid = (
    newMax?: number,
    newMin?: number,
    upperLimit?: number
  ) => {
    if (newMin === undefined) {
      return false;
    }

    if (newMin < 1) return true;
    if (upperLimit && newMin >= (newMax || upperLimit)) return true;
    if (upperLimit && newMin > upperLimit) return true;

    return false;
  };

  const isAddtoRangeButtonInValid = (
    newMax?: number,
    newMin?: number,
    upperLimit?: number
  ) => {
    if (newMax === undefined || newMin === undefined) {
      return true;
    }
    return (
      isMaxInvalid(newMax, newMin, upperLimit) ||
      isMinInvalid(newMax, newMin, upperLimit)
    );
  };

  return (
    <div className="data-card">
      <h3 data-cy={`${addRangeTitle.replaceAll(" ", "")}`}>{addRangeTitle}</h3>
      <div className="grid grid-cols-3 gap-4 form-control mt-4">
        <div>
          <HexInput
            label={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.minId(
              labelPrefix
            )}
            max={
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            }
            min={XSSI_RANGE_MIN_VALUE}
            disabled={false}
            value={min}
            onChange={(val) => setMin(val || val === 0 ? +val : undefined)}
            invalid={isMinInvalid(
              max,
              min,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            invalidText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.minInvalidText(
              labelPrefix,
              XSSI_RANGE_MIN_VALUE,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            tooltipText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.minToolTipText(
              labelPrefix,
              XSSI_RANGE_MIN_VALUE,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            required
          />
        </div>

        <div>
          <HexInput
            label={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.maxId(
              labelPrefix
            )}
            max={
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            }
            min={XSSI_RANGE_MIN_VALUE}
            disabled={false}
            value={max}
            onChange={(val) => setMax(val || val === 0 ? +val : undefined)}
            invalid={isMaxInvalid(
              max,
              min,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            invalidText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.maxInvalidText(
              labelPrefix,
              XSSI_RANGE_MIN_VALUE,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            tooltipText={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels.maxToolTipText(
              labelPrefix,
              XSSI_RANGE_MIN_VALUE,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            required
          />
        </div>

        <div className="mt-5 range-button">
          <Button
            className="w-full justify-center"
            type="button"
            disabled={isAddtoRangeButtonInValid(
              max,
              min,
              labelPrefix === "Group"
                ? XSSI_GROUP_RANGE_MAX_VALUE
                : XSSI_UNIT_RANGE_MAX_VALUE
            )}
            icon={Icons.Add}
            label={CREATE_XSSI_CONNECTION_TRANSLATIONS.ranges.addToRange}
            loading={loading}
            onClick={() => {
              if (min !== undefined && max !== undefined) {
                addToRange(min, max);
                setMax(undefined);
                setMin(undefined);
              }
            }}
            dataCy={`${addRangeTitle.replaceAll(" ", "")}-`}
          />
        </div>
      </div>
    </div>
  );
};

export default AddRange;
